import type { ADFEntity } from '@atlaskit/adf-utils/types';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { isSkillTagExtension } from '@atlassian/conversation-assistant-content-transformer/utils/skills';

/**
 * Recursively extracts the human-readable text from an ADF node. Skill-tag
 * inline extensions are flattened to their `slug` so the result reflects
 * what the user actually sees rendered in the bubble.
 */
export const extractText = (node: ADFEntity | undefined | null): string => {
	if (!node) {
		return '';
	}
	if (node.type === 'text') {
		return node.text || '';
	}
	if (
		node.type === 'inlineExtension' &&
		node.attrs &&
		isSkillTagExtension(node.attrs?.extensionType, node.attrs?.extensionKey)
	) {
		return node.attrs?.parameters?.slug || '';
	}
	// Smart-link cards carry no text node but ContentRenderer can render them.
	// Surface the URL so `isMessageEmpty` keeps the bubble; a missing URL still
	// returns a non-empty sentinel so a malformed-but-renderable card isn't dropped.
	if (
		FeatureGates.getExperimentValue(
			'rovo_chat_render_card_only_human_messages',
			'isEnabled',
			false,
		) &&
		(node.type === 'inlineCard' || node.type === 'blockCard' || node.type === 'embedCard')
	) {
		const url = typeof node.attrs?.url === 'string' ? node.attrs.url : '';
		return url || ' ';
	}
	if (Array.isArray(node.content)) {
		return node.content.map(extractText).join('');
	}
	return '';
};
